import SettingsShell from "@/modules/settings/SettingsShell";
import { useGetImage } from "@/utilities/helper/getImage";
import { merchantSiteUrl } from "@/utilities/helper/merchantSite";
import {
  Alert,
  Button,
  Card,
  CopyButton,
  Grid,
  Group,
  Image,
  SimpleGrid,
  Skeleton,
  Stack,
  Switch,
  Text,
  TextInput,
  Textarea,
  Container,
  UnstyledButton,
} from "@mantine/core";
import { useDebouncedValue } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import { IconCheck, IconCopy, IconDownload, IconExternalLink, IconQrcode, IconRestore } from "@tabler/icons-react";
import QRCode from "qrcode";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import useGetMerchant from "../../hooks/useGetMerchant";
import { BANNER_HEIGHT, BANNER_TEMPLATES, BANNER_WIDTH, renderBanner } from "./banners";

const QR_COLOR = "#1b1f3b";
const DEFAULT_DESCRIPTION = "برای مشاهده خدمات و رزرو آنلاین نوبت، کد را با دوربین گوشی اسکن کنید";
const TITLE_MAX = 40;
const DESCRIPTION_MAX = 140;

const saveImage = (href: string, fileName: string) => {
  const link = document.createElement("a");
  link.href = href;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
};

function SiteQrCode() {
  const { data: merchant, isLoading } = useGetMerchant();
  const subDomain = merchant?.businessInfo?.subDomain;
  const merchantTitle = merchant?.businessInfo?.title || "سایت مجموعه";
  const url = merchantSiteUrl(subDomain);
  const { imageUrl: logoUrl, isLoading: logoLoading } = useGetImage({ image: merchant?.logo, bucket: "merchant" });
  const [logoImage, setLogoImage] = useState<HTMLImageElement | null>(null);
  const [showLogo, setShowLogo] = useState(true);
  const logo = showLogo ? logoImage : null;

  const [templateId, setTemplateId] = useState(BANNER_TEMPLATES[0].id);
  const [titleInput, setTitleInput] = useState<string>();
  const [description, setDescription] = useState(DEFAULT_DESCRIPTION);
  const title = titleInput ?? merchantTitle;
  const template = BANNER_TEMPLATES.find((item) => item.id === templateId) ?? BANNER_TEMPLATES[0];

  const [debouncedTitle] = useDebouncedValue(title, 300);
  const [debouncedDescription] = useDebouncedValue(description, 300);

  const [qr, setQr] = useState<string>();
  const [preview, setPreview] = useState<string>();
  const [thumbnails, setThumbnails] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setLogoImage(null);
    if (!logoUrl) return;
    let active = true;
    const image = new window.Image();
    image.onload = () => active && setLogoImage(image);
    image.src = logoUrl;
    return () => {
      active = false;
    };
  }, [logoUrl]);

  useEffect(() => {
    if (!url) return;
    let active = true;
    QRCode.toDataURL(url, { width: 640, margin: 2, errorCorrectionLevel: "M", color: { dark: QR_COLOR, light: "#ffffff" } })
      .then((value) => active && setQr(value))
      .catch(() => active && setQr(undefined));
    return () => {
      active = false;
    };
  }, [url]);

  useEffect(() => {
    if (!url) return;
    let active = true;
    renderBanner(template, { url, title: debouncedTitle, description: debouncedDescription, logo }, 0.5)
      .then((canvas) => active && setPreview(canvas.toDataURL("image/png")))
      .catch(() => active && setPreview(undefined));
    return () => {
      active = false;
    };
  }, [url, template, debouncedTitle, debouncedDescription, logo]);

  useEffect(() => {
    if (!url) return;
    let active = true;
    const content = { url, title: debouncedTitle, description: debouncedDescription, logo };
    Promise.all(
      BANNER_TEMPLATES.map(async (item) => [item.id, (await renderBanner(item, content, 0.2)).toDataURL("image/png")] as const),
    )
      .then((entries) => active && setThumbnails(Object.fromEntries(entries)))
      .catch(() => undefined);
    return () => {
      active = false;
    };
  }, [url, debouncedTitle, debouncedDescription, logo]);

  const downloadBanner = async () => {
    setSaving(true);
    try {
      const canvas = await renderBanner(template, { url, title, description, logo });
      const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, "image/png"));
      if (!blob) throw new Error("Empty image");
      const href = URL.createObjectURL(blob);
      saveImage(href, `qr-banner-${subDomain}-${template.id}.png`);
      setTimeout(() => URL.revokeObjectURL(href), 1000);
    } catch {
      notifications.show({ color: "red", message: "ساخت تصویر ناموفق بود. دوباره تلاش کنید." });
    } finally {
      setSaving(false);
    }
  };

  const resetText = () => {
    setTitleInput(undefined);
    setDescription(DEFAULT_DESCRIPTION);
  };

  return (
    <SettingsShell title="کد QR" description="یک طرح انتخاب کنید، عنوان را بنویسید و بنر آماده‌ی چاپ را دانلود کنید؛ مشتری با اسکن کد وارد سایت می‌شود.">
      <Container w="100%" size="md" pb="xl" px={0}>
        <Stack>

          {isLoading ? (
            <Skeleton h={420} radius="md" />
          ) : !url ? (
            <Alert color="yellow" icon={<IconQrcode size={20} />} title="آدرس سایت ثبت نشده است">
              <Text size="sm" mb="sm">
                برای ساخت کیوآر کد، ابتدا «آدرس دامنه مجموعه» را در اطلاعات کسب و کار وارد کنید.
              </Text>
              <Button component={Link} to="/dashboard/profile/edit/merchantProfile" size="xs" variant="light">
                رفتن به اطلاعات کسب و کار
              </Button>
            </Alert>
          ) : (
            <Grid gutter="md">
              <Grid.Col span={{ base: 12, md: 6 }}>
                <Stack>
                  <Card withBorder radius="md" padding="md">
                    <Text fw={700} mb="sm">انتخاب طرح</Text>
                    <SimpleGrid cols={{ base: 3, sm: 6, md: 4 }} spacing="xs">
                      {BANNER_TEMPLATES.map((item) => {
                        const selected = item.id === template.id;
                        return (
                          <UnstyledButton key={item.id} onClick={() => setTemplateId(item.id)} aria-pressed={selected}>
                            <Stack gap={4} align="center">
                              {thumbnails[item.id] ? (
                                <Image
                                  src={thumbnails[item.id]}
                                  alt={item.name}
                                  radius="sm"
                                  style={{
                                    aspectRatio: `${BANNER_WIDTH} / ${BANNER_HEIGHT}`,
                                    outline: selected ? "3px solid var(--mantine-primary-color-filled)" : "1px solid var(--mantine-color-gray-3)",
                                    outlineOffset: 2,
                                  }}
                                />
                              ) : (
                                <Skeleton radius="sm" style={{ aspectRatio: `${BANNER_WIDTH} / ${BANNER_HEIGHT}` }} />
                              )}
                              <Text size="xs" fw={selected ? 700 : 400} c={selected ? undefined : "dimmed"}>
                                {item.name}
                              </Text>
                            </Stack>
                          </UnstyledButton>
                        );
                      })}
                    </SimpleGrid>
                  </Card>

                  <Card withBorder radius="md" padding="md">
                    <Stack gap="sm">
                      <TextInput
                        label="عنوان بنر"
                        value={title}
                        maxLength={TITLE_MAX}
                        onChange={(event) => setTitleInput(event.currentTarget.value)}
                        description={`${title.length}/${TITLE_MAX}`}
                      />
                      <Textarea
                        label="توضیحات"
                        value={description}
                        maxLength={DESCRIPTION_MAX}
                        autosize
                        minRows={2}
                        maxRows={4}
                        onChange={(event) => setDescription(event.currentTarget.value)}
                        description={`${description.length}/${DESCRIPTION_MAX} — حداکثر سه خط روی بنر نمایش داده می‌شود`}
                      />
                      {logoImage ? (
                        <Switch
                          label="نمایش لوگوی مجموعه روی بنر"
                          checked={showLogo}
                          onChange={(event) => setShowLogo(event.currentTarget.checked)}
                        />
                      ) : (
                        !logoLoading && (
                          <Text size="xs" c="dimmed">
                            برای نمایش لوگو روی بنر، لوگوی مجموعه را در{" "}
                            <Text component={Link} to="/dashboard/profile/edit/merchantProfile" size="xs" c="blue">
                              اطلاعات کسب و کار
                            </Text>{" "}
                            بارگذاری کنید.
                          </Text>
                        )
                      )}
                      <Group justify="flex-end">
                        <Button variant="subtle" size="xs" leftSection={<IconRestore size={14} />} onClick={resetText}>
                          متن پیش‌فرض
                        </Button>
                      </Group>
                    </Stack>
                  </Card>
                </Stack>
              </Grid.Col>

              <Grid.Col span={{ base: 12, md: 6 }}>
                <Card withBorder radius="md" padding="lg">
                  <Stack align="center" gap="sm">
                    {preview ? (
                      <Image
                        src={preview}
                        alt={`بنر کیوآر کد ${url}`}
                        radius="md"
                        maw={360}
                        w="100%"
                        style={{ boxShadow: "var(--mantine-shadow-md)" }}
                      />
                    ) : (
                      <Skeleton maw={360} w="100%" radius="md" style={{ aspectRatio: `${BANNER_WIDTH} / ${BANNER_HEIGHT}` }} />
                    )}
                    <Group justify="center" gap="xs" mt="xs">
                      <Button leftSection={<IconDownload size={16} />} loading={saving} disabled={!preview} onClick={downloadBanner}>
                        دانلود بنر
                      </Button>
                      <Button variant="light" leftSection={<IconQrcode size={16} />} disabled={!qr}
                        onClick={() => qr && saveImage(qr, `qr-code-${subDomain}.png`)}>
                        فقط کد
                      </Button>
                    </Group>
                    <Group justify="center" gap="xs">
                      <CopyButton value={url} timeout={1500}>
                        {({ copied, copy }) => (
                          <Button variant="subtle" size="xs" color={copied ? "teal" : undefined}
                            leftSection={copied ? <IconCheck size={14} /> : <IconCopy size={14} />} onClick={copy}>
                            {copied ? "کپی شد" : "کپی لینک"}
                          </Button>
                        )}
                      </CopyButton>
                      <Button component="a" href={url} target="_blank" rel="noreferrer" variant="subtle" size="xs"
                        leftSection={<IconExternalLink size={14} />}>
                        باز کردن سایت
                      </Button>
                    </Group>
                    <Text size="xs" c="dimmed" ta="center">
                      «دانلود بنر» تصویر {BANNER_WIDTH}×{BANNER_HEIGHT} پیکسل می‌سازد که برای چاپ A5 و A4 مناسب است؛ «فقط کد» تصویر خود
                      کیوآر کد است.
                    </Text>
                  </Stack>
                </Card>
              </Grid.Col>
            </Grid>
          )}
        </Stack>
      </Container>
    </SettingsShell>
  );
}

export default SiteQrCode;
